import { useCallback, useState } from 'react';
import { liveViewSupport, type LiveEvent, type Platform } from '@1qa/shared';

const PREF_KEY = '1qa.watchLive';

function readPref(): boolean {
  try {
    return window.localStorage.getItem(PREF_KEY) === '1';
  } catch {
    return false;
  }
}

function writePref(on: boolean) {
  try {
    window.localStorage.setItem(PREF_KEY, on ? '1' : '0');
  } catch {
    /* storage unavailable: keep the choice for this page only */
  }
}

/** The user's last "Watch live" choice, remembered per browser. */
export function useWatchLivePreference(): [boolean, (on: boolean) => void] {
  const [on, setOn] = useState(readPref);
  const set = useCallback((next: boolean) => {
    setOn(next);
    writePref(next);
  }, []);
  return [on, set];
}

export interface LiveToggleState {
  /** Whether the toggle can be used for this project. */
  available: boolean;
  /** Effective value: the preference, but never on when unavailable. */
  checked: boolean;
  /** Why it is unavailable (helper text / tooltip). */
  reason?: string;
}

/** Combines the remembered preference with what the project supports. */
export function liveToggleState(
  project: { platform: Platform; settings?: unknown } | null | undefined,
  preference: boolean,
): LiveToggleState {
  if (!project) return { available: false, checked: false, reason: 'Loading project…' };
  const support = liveViewSupport(project);
  return support.supported
    ? { available: true, checked: preference }
    : { available: false, checked: false, reason: support.reason };
}

/** Parses one SSE data payload into a LiveEvent (null for anything unexpected). */
export function parseLiveEvent(data: string): LiveEvent | null {
  try {
    const e = JSON.parse(data) as LiveEvent;
    return e && typeof e === 'object' && typeof e.type === 'string' ? e : null;
  } catch {
    return null;
  }
}

/** Retry delays (ms) after a lost connection; after the last one the user reconnects manually. */
export const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 15000];
